import React, { useState } from "react";
import { Button, Modal, SelectChangeEvent } from "@mui/material";
import musicIcon from "@/assets/icons/music.svg";
import imgIcon from "@/assets/icons/image-upload.svg";
import styles from "./AddMusicModal.module.scss";
import Input from "@/components/Input/Input";
import fileIcon from "@/assets/icons/txt-upload.svg";
import Textarea from "@/components/Textarea/Textarea";
import { notification } from "antd";
import CustomSelect from "@/components/Select/Select";
import { MediaAccessType } from "@/store/models/video.models";
type Props = {
  isOpenAddItemModal: boolean;
  setIsOpenAddItemModal: (isOpenAddItemModal: boolean) => void;
  onAddAudio: (formData: FormData) => Promise<void>;
};

export const typeOptions = Object.values(MediaAccessType)
  .slice(0, 2)
  .map((type) => ({
    value: type,
    label: type === MediaAccessType.BASE ? "Base" : "Premium",
  }));

const AddMusicModal = ({
  isOpenAddItemModal,
  setIsOpenAddItemModal,
  onAddAudio,
}: Props) => {
  const [musicSrc, setMusicSrc] = useState<File | string>("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [musicImg, setMusicImg] = useState<File | string>("");
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [audioName, setAudioName] = useState<string | null>(null);
  const [subtitles, setSubtitles] = useState<File | string>("");
  const [subtitleName, setSubtitleName] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState(MediaAccessType.BASE);

  const handleMusicImgChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setMusicImg(file);
      setThumbnailPreview(URL.createObjectURL(file));
    } else {
      setMusicImg("");
      setThumbnailPreview(null);
    }
  };

  const handleAudioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setMusicSrc(file);
      setAudioName(file.name);
    } else {
      setMusicSrc("");
      setAudioName(null);
    }
  };

  const handleSubtitlesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSubtitles(file);
      setSubtitleName(file.name);
    } else {
      setSubtitles("");
      setSubtitleName(null);
    }
  };

  const handleRoleChange = (event: SelectChangeEvent<MediaAccessType>) => {
    const type = event.target.value as MediaAccessType;
    setSelectedRole(type);
  };

  const handleSave = async () => {
    const formData = new FormData();
    if (musicSrc instanceof File) {
      formData.append("musicSrc", musicSrc);
    } else if (typeof musicSrc === "string" && musicSrc.trim() !== "") {
      formData.append("musicSrcUrl", musicSrc as string);
    } else {
      notification.error({
        message: "Error",
        description: "You must provide either a song file or a song URL.",
      });
      return;
    }

    formData.append("title", title);
    formData.append("description", description);
    formData.append("accessType", selectedRole);

    if (musicImg instanceof File) {
      formData.append("musicImg", musicImg);
    } else {
      formData.append("musicImg", musicImg as string);
    }

    if (subtitles instanceof File) {
      formData.append("subtitles", subtitles);
    } else {
      formData.append("subtitlesUrl", subtitles as string);
    }
    setIsOpenAddItemModal(false);
    try {
      await onAddAudio(formData);
      resetForm();
      notification.success({
        message: "Success",
        description: "Song added successfully!",
      });
    } catch (error: any) {
      notification.error({
        message: "Error",
        description:
          error?.message || "Failed to create new song. Please try again.",
      });
    }
  };

  const resetForm = () => {
    setMusicSrc("");
    setTitle("");
    setDescription("");
    setMusicImg("");
    setThumbnailPreview(null);
    setAudioName(null);
    setSubtitles("");
    setSubtitleName(null);
    setIsOpenAddItemModal(false);
  };

  return (
    <Modal
      aria-labelledby="modal-adding-item-to-content"
      aria-describedby="Add an item to the system. Enter the required data and click save"
      open={isOpenAddItemModal}
      onClose={() => setIsOpenAddItemModal(false)}
    >
      <div className={styles.modalAddContainer}>
        <h3 className={styles.modalTitle}>Add item</h3>

        <label
          className={styles.modalAddItem}
          htmlFor="audio"
          id="dropcontainer"
        >
          <img className={styles.modalImg} src={musicIcon} alt="audio" />
          <input
            id="audio"
            type="file"
            accept="audio/*"
            onChange={handleAudioChange}
          />

          <span className={styles.modalPrimaryText}>
            or drag and drop the file here (.mp3)
          </span>
        </label>
        {audioName && (
          <p className={styles.previewVideo}>Selected audio: {audioName}</p>
        )}

        {/* This functionality is not working yet */}

        {/* <p className={styles.modalHelper}>or add a link to the file</p>
        <Input
          label=""
          value={musicSrc instanceof File ? "" : (musicSrc as string)}
          onChange={(e) => setMusicSrc(e.target.value)}
          placeholder="Enter your link"
          disabled={musicSrc instanceof File}
        /> */}

        <Input
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Enter the title"
        />

        <Textarea
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />

        <div className={styles.settings}>
          <CustomSelect
            label="Access type"
            options={typeOptions}
            value={selectedRole}
            onChange={handleRoleChange}
          />
        </div>

        <label
          className={styles.modalAddItem}
          htmlFor="musicImg"
          id="dropcontainer"
        >
          <img className={styles.modalImg} src={imgIcon} alt="musicImg" />
          <input
            id="musicImg"
            type="file"
            accept="image/*"
            onChange={handleMusicImgChange}
          />

          <span className={styles.modalPrimaryText}>
            or drag and drop the thumbnail here (*.png)
          </span>
        </label>
        {thumbnailPreview && (
          <img
            className={styles.thumbnailPreview}
            src={thumbnailPreview}
            alt="Thumbnail Preview"
            width={100}
            height={100}
          />
        )}

        {/* This functionality is not working yet */}

        {/* <p className={styles.modalHelper}>or add a link to the file</p>
        <Input
          label=""
          value={musicImg instanceof File ? "" : (musicImg as string)}
          onChange={(e) => setMusicImg(e.target.value)}
          placeholder="Enter the thumbnail link"
          disabled={musicImg instanceof File}
        /> */}

        <label
          className={styles.modalAddItem}
          htmlFor="subtitles"
          id="dropcontainer"
        >
          <img className={styles.modalImg} src={fileIcon} alt="subtitles" />
          <input
            id="subtitles"
            type="file"
            accept=".srt"
            onChange={handleSubtitlesChange}
          />
          <span className={styles.modalPrimaryText}>
            or drag and drop the subtitles here (*.srt)
          </span>
        </label>

        {subtitleName && (
          <p className={styles.previewVideo}>
            Selected subtitles: {subtitleName}
          </p>
        )}

        {/* This functionality is not working yet */}

        {/* <p className={styles.modalHelper}>or add a link to the file</p>
        <Input
          label=""
          value={subtitles instanceof File ? "" : (subtitles as string)}
          onChange={(e) => setSubtitles(e.target.value)}
          placeholder="Enter the subtitles link"
          disabled={subtitles instanceof File}
        /> */}

        <div className={styles.btns}>
          <Button
            sx={{
              textTransform: "capitalize",
              height: "44px",
              width: "150px",
              backgroundColor: "#008000",
              borderRadius: "12px",
              "&:hover": {
                backgroundColor: "#008000",
              },
            }}
            variant="contained"
            onClick={handleSave}
          >
            Save
          </Button>

          <Button
            variant="outlined"
            sx={{
              textTransform: "capitalize",
              height: "44px",
              width: "150px",
              border: "1px solid #5570F1",
              color: "#5570F1",
              borderRadius: "12px",
            }}
            onClick={() => setIsOpenAddItemModal(false)}
          >
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AddMusicModal;

